import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, PlayCircle, ChevronDown, ChevronRight, Wand2, Upload, RotateCw } from "lucide-react";
import { SCSection, fmtDate } from "./Common";
import {
    scAdminListBatches, scAdminRunJob, scAdminPromoteUnmapped,
    scAdminUploadDataFile, scAdminRerunBatch, scAdminSchedulerStatus,
} from "../../../lib/sifcontrolApi";

const StatusPill = ({ status }) => {
    const map = {
        completed: "bg-green-100 text-green-800",
        completed_with_errors: "bg-yellow-100 text-yellow-800",
        failed: "bg-red-100 text-red-800",
        running: "bg-blue-100 text-blue-800",
    };
    const cls = map[status] || "bg-oth-bg-2 text-oth-ink3";
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-body uppercase tracking-wider ${cls}`}>{status || "—"}</span>;
};

const Row = ({ batch, onChanged }) => {
    const [open, setOpen] = useState(false);
    const [promoting, setPromoting] = useState(false);
    const [rerunning, setRerunning] = useState(false);

    const promote = async (e) => {
        e.stopPropagation();
        const unmapped = batch.unmapped_count || 0;
        if (!unmapped) {
            toast.info("No unmapped rows to promote.");
            return;
        }
        if (!window.confirm(`Promote ${unmapped} unmapped SIF${unmapped === 1 ? "" : "s"} to masters under the "Unassigned" AMC? You can re-parent them in the SIFs tab afterwards.`)) return;
        setPromoting(true);
        try {
            const r = await scAdminPromoteUnmapped(batch.id, null);
            toast.success(`${r.created} SIF${r.created === 1 ? "" : "s"} created${r.skipped_duplicates ? `, ${r.skipped_duplicates} skipped as duplicates` : ""}.`);
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Promote failed");
        } finally { setPromoting(false); }
    };

    const rerun = async (e) => {
        e.stopPropagation();
        if (!window.confirm(`Re-run this ${batch.data_type?.toUpperCase()} batch? This will fetch fresh data from AMFI and upsert.`)) return;
        setRerunning(true);
        try {
            const r = await scAdminRerunBatch(batch.id);
            if (r.status === "failed") toast.error(`Re-run failed: ${r.error_message || ""}`);
            else toast.success(`Re-run ${r.status} — ${r.records_inserted} inserted / ${r.records_updated} updated`);
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Re-run failed");
        } finally { setRerunning(false); }
    };

    const canPromote = batch.data_type === "nav" && (batch.unmapped_count || 0) > 0;
    const canRerun = batch.mode !== "upload" && ["nav", "aum", "ter"].includes(batch.data_type);

    return (
        <div className="border-b border-oth-line/60 last:border-b-0" data-testid={`sc-batch-row-${batch.id}`}>
            <div
                className="grid grid-cols-[100px_1fr_130px_100px_100px_80px_40px] items-center px-5 py-3 cursor-pointer hover:bg-oth-bg-2/40"
                onClick={() => setOpen((o) => !o)}
            >
                <div className="text-xs font-body font-medium uppercase text-oth-ink2">{batch.data_type}</div>
                <div className="text-xs font-body text-oth-ink2 truncate">{fmtDate(batch.started_at)}</div>
                <div><StatusPill status={batch.status} /></div>
                <div className="text-xs font-body text-oth-ink2">+{batch.records_inserted}</div>
                <div className="text-xs font-body text-oth-ink3">{batch.records_processed} rows</div>
                <div className="text-[11px] font-body text-oth-ink3">{batch.fetch_mode || "—"}</div>
                <div className="text-oth-ink3">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</div>
            </div>
            {open && (
                <div className="px-5 py-3 bg-oth-bg-2/40 text-[12px] font-body text-oth-ink2 space-y-2" data-testid={`sc-batch-detail-${batch.id}`}>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div><span className="text-oth-ink3">Processed:</span> {batch.records_processed}</div>
                        <div><span className="text-oth-ink3">Inserted:</span> {batch.records_inserted}</div>
                        <div><span className="text-oth-ink3">Updated:</span> {batch.records_updated}</div>
                        <div><span className="text-oth-ink3">Skipped (unmapped):</span> {batch.records_skipped}</div>
                        <div><span className="text-oth-ink3">Failed:</span> {batch.records_failed}</div>
                        <div><span className="text-oth-ink3">Duration:</span> {batch.duration_ms ? `${(batch.duration_ms / 1000).toFixed(1)}s` : "—"}</div>
                        <div><span className="text-oth-ink3">Mode:</span> {batch.fetch_mode || "—"}</div>
                        <div><span className="text-oth-ink3">Completed:</span> {batch.completed_at ? fmtDate(batch.completed_at) : "—"}</div>
                    </div>
                    {batch.error_message && (
                        <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 px-3 py-2 whitespace-pre-wrap">{batch.error_message}</div>
                    )}
                    {batch.unmapped_sample?.length > 0 && (
                        <div>
                            <div className="text-oth-ink3 mb-1">Unmapped sample ({batch.unmapped_count} total):</div>
                            <div className="flex flex-wrap gap-1.5">
                                {batch.unmapped_sample.map((s, i) => (
                                    <span key={`${batch.id}-un-${i}`} className="px-2 py-0.5 rounded-full bg-white border border-oth-line/70 text-[11px]">{s}</span>
                                ))}
                            </div>
                        </div>
                    )}
                    {batch.source_filename && (
                        <div><span className="text-oth-ink3">Source file:</span> <code className="text-oth-ink">{batch.source_filename}</code></div>
                    )}
                    <div className="pt-2 flex flex-wrap gap-2">
                        {canRerun && (
                            <button
                                onClick={rerun}
                                disabled={rerunning}
                                className="btn-pill !text-xs !py-1.5 !px-3"
                                data-testid={`sc-batch-rerun-${batch.id}`}
                            >
                                <RotateCw size={12} className={rerunning ? "animate-spin" : ""} /> {rerunning ? "Re-running…" : "Re-run"}
                            </button>
                        )}
                        {batch.mode === "upload" && (
                            <span className="text-[11px] text-oth-ink3 self-center">To re-run an upload batch, re-upload the file above.</span>
                        )}
                    </div>
                    {canPromote && (
                        <div className="pt-2">
                            <button
                                onClick={promote}
                                disabled={promoting}
                                className="btn-pill btn-pill-dark !text-xs !py-1.5 !px-3"
                                data-testid={`sc-batch-promote-${batch.id}`}
                            >
                                <Wand2 size={12} /> {promoting ? "Promoting…" : `Promote ${batch.unmapped_count} unmapped to SIF masters`}
                            </button>
                            <div className="text-[11px] text-oth-ink3 mt-1.5">
                                Creates SIF entries under an auto-created “Unassigned” AMC. Re-parent to the correct AMC from the SIFs tab afterwards.
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default function JobsTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(null);
    const [filter, setFilter] = useState("");
    const [uploading, setUploading] = useState(null);
    const aumInputRef = useRef(null);
    const terInputRef = useRef(null);
    const navInputRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await scAdminListBatches({ data_type: filter, limit: 50 });
            setItems(r.items || []);
        } catch { toast.error("Could not load jobs"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const run = async (t) => {
        setRunning(t);
        try {
            const batch = await scAdminRunJob(t);
            if (batch.status === "failed") toast.error(`${t.toUpperCase()} failed: ${batch.error_message || ""}`);
            else if (batch.status === "completed_with_errors") toast.warning(`${t.toUpperCase()} completed with errors`);
            else toast.success(`${t.toUpperCase()} completed`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to run");
        } finally { setRunning(null); }
    };

    const handleUpload = async (data_type, file) => {
        if (!file) return;
        if (file.size > 15 * 1024 * 1024) { toast.error("File too large — 15 MB max."); return; }
        if (!/\.xlsx?$/i.test(file.name)) { toast.error("Please choose a .xlsx file"); return; }
        setUploading(data_type);
        try {
            const batch = await scAdminUploadDataFile(data_type, file);
            if (batch.status === "failed") {
                toast.error(`${data_type.toUpperCase()} upload failed: ${batch.error_message || ""}`);
            } else {
                toast.success(
                    `${data_type.toUpperCase()} uploaded — ${batch.records_inserted} inserted, ${batch.records_updated} updated${batch.records_skipped ? `, ${batch.records_skipped} unmapped` : ""}`,
                );
            }
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(null);
        }
    };

    return (
        <div data-testid="sc-admin-jobs">
            <SCSection
                title="Data ingestion"
                subtitle="Run AMFI NAV ingestion, or upload AAUM and TER Excel files directly. All runs are idempotent."
                right={
                    <div className="flex items-center gap-2">
                        <button onClick={() => run("nav")} disabled={!!running} className="btn-pill btn-pill-dark !text-xs !py-1.5 !px-3" data-testid="sc-jobs-run-nav">
                            {running === "nav" ? <RefreshCw size={12} className="animate-spin" /> : <PlayCircle size={12} />} Run NAV
                        </button>
                    </div>
                }
                testId="sc-jobs-header"
            />

            <div className="rounded-2xl border border-oth-line/70 bg-white p-4 mb-6" data-testid="sc-jobs-uploads">
                <div className="text-[10px] uppercase tracking-[0.2em] text-oth-ink3 font-body mb-2">Upload from AMFI Excel</div>
                <div className="text-[12px] font-body text-oth-ink2 mb-3">
                    Drop the AAUM / TER XLSX files published by AMFI. AAUM uses <code>AMFI Code</code>; TER uses <code>Investment strategy Name</code> to find matching SIFs (Regular BER → Regular plans, Direct BER → Direct plans).
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <input
                        ref={aumInputRef}
                        type="file"
                        accept=".xlsx"
                        className="hidden"
                        onChange={(e) => { handleUpload("aum", e.target.files?.[0]); e.target.value = ""; }}
                        data-testid="sc-jobs-upload-aum-input"
                    />
                    <button
                        onClick={() => aumInputRef.current?.click()}
                        disabled={!!uploading}
                        className="btn-pill !text-xs !py-1.5 !px-3"
                        data-testid="sc-jobs-upload-aum"
                    >
                        {uploading === "aum" ? <RefreshCw size={12} className="animate-spin" /> : <Upload size={12} />}
                        {uploading === "aum" ? "Uploading AUM…" : "Upload AUM (.xlsx)"}
                    </button>
                    <input
                        ref={terInputRef}
                        type="file"
                        accept=".xlsx"
                        className="hidden"
                        onChange={(e) => { handleUpload("ter", e.target.files?.[0]); e.target.value = ""; }}
                        data-testid="sc-jobs-upload-ter-input"
                    />
                    <button
                        onClick={() => terInputRef.current?.click()}
                        disabled={!!uploading}
                        className="btn-pill !text-xs !py-1.5 !px-3"
                        data-testid="sc-jobs-upload-ter"
                    >
                        {uploading === "ter" ? <RefreshCw size={12} className="animate-spin" /> : <Upload size={12} />}
                        {uploading === "ter" ? "Uploading TER…" : "Upload TER (.xlsx)"}
                    </button>
                    <input
                        ref={navInputRef}
                        type="file"
                        accept=".xlsx,.csv"
                        className="hidden"
                        onChange={(e) => { handleUpload("nav", e.target.files?.[0]); e.target.value = ""; }}
                        data-testid="sc-jobs-upload-nav-input"
                    />
                    <button
                        onClick={() => navInputRef.current?.click()}
                        disabled={!!uploading}
                        className="btn-pill btn-pill-dark !text-xs !py-1.5 !px-3"
                        data-testid="sc-jobs-upload-nav"
                    >
                        {uploading === "nav" ? <RefreshCw size={12} className="animate-spin" /> : <Upload size={12} />}
                        {uploading === "nav" ? "Uploading NAV history…" : "Upload NAV history"}
                    </button>
                    <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/sifcontrol/admin/nav-template`}
                        className="text-[11px] font-body text-oth-ink3 hover:text-oth-ink underline underline-offset-2"
                        data-testid="sc-jobs-nav-template"
                    >
                        Download NAV template
                    </a>
                </div>
            </div>

            <div className="flex items-center gap-3 mb-4">
                <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-9 px-4 rounded-full border border-oth-line/70 bg-white text-xs focus:outline-none focus:border-oth-blue" data-testid="sc-jobs-filter">
                    <option value="">All types</option>
                    <option value="nav">NAV</option>
                    <option value="aum">AUM</option>
                    <option value="ter">TER</option>
                </select>
                <button onClick={load} className="btn-pill !text-xs !py-1.5 !px-3" data-testid="sc-jobs-refresh"><RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh</button>
            </div>

            {loading ? (
                <div className="p-10 text-center text-sm font-body text-oth-ink3">Loading…</div>
            ) : items.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-oth-line/70 p-8 text-center text-sm font-body text-oth-ink3" data-testid="sc-jobs-empty">
                    No ingestion runs yet. Trigger one above.
                </div>
            ) : (
                <div className="rounded-2xl border border-oth-line/70 bg-white overflow-hidden">
                    <div className="grid grid-cols-[100px_1fr_130px_100px_100px_80px_40px] text-[10px] uppercase tracking-[0.16em] text-oth-ink3 font-body px-5 py-3 bg-oth-bg-2/60 border-b border-oth-line/70">
                        <div>Type</div>
                        <div>Started</div>
                        <div>Status</div>
                        <div>Inserted</div>
                        <div>Rows</div>
                        <div>Mode</div>
                        <div />
                    </div>
                    {items.map((b) => <Row key={b.id} batch={b} onChanged={load} />)}
                </div>
            )}
        </div>
    );
}
